import {
    TemplateElement,
    html,
    defineElement,
    when,
    classMap,
    optionalAttribute,
    unsafeHTML,
} from '@webtides/element-js';
import style from './choice-chip.css';
import '../icon/icon.js';

/**
 * Generic, surface-aware choice chip / link tile.
 * Colors come from the per-surface --chip-* tokens (css/base/surfaces.css).
 *
 * @property {string} headline
 * @property {string} text
 * @property {string} leadingIcon - icon name before the text; overridden by slot="leading"
 * @property {string} trailingIcon - icon name after the text; overridden by slot="trailing"
 * @property {'sm'|'md'|'lg'} size - chip size (use one size across all breakpoints)
 * @property {boolean} checked - selected/active state
 * @property {boolean} disabled - non-interactive state
 * @property {LinkProperties} link - Link object with url, text, trackingLabel
 */
export default class ChoiceChip extends TemplateElement {
    constructor() {
        super({ styles: [style], shadowRender: true });
    }

    properties() {
        return {
            headline: '',
            text: '',
            leadingIcon: '',
            trailingIcon: '',
            size: 'md',
            checked: false,
            disabled: false,
            link: null,
        };
    }

    afterUpdate() {
        const states = this._internals.states;
        if (this.checked) states.add('checked');
        else states.delete('checked');
        if (this.disabled) states.add('disabled');
        else states.delete('disabled');
    }

    iconTemplate(name, iconClass) {
        return when(name, html`<mb-icon size="inherit" name="${name}" class="shrink-0 ${iconClass}"></mb-icon>`);
    }

    template() {
        const chipClasses = {
            'px-16 py-12': this.size === 'sm',
            'p-16': this.size === 'md',
            'px-16 py-24': this.size === 'lg',
        };
        const leadingIconClasses = {
            'size-24': this.size === 'sm',
            'size-32': this.size === 'md' || this.size === 'lg',
        };
        const trailingIconClasses = {
            'size-24': true,
        };
        const headlineClasses = {
            'fix-body2-medium': this.size === 'sm',
            'fix-body1-medium': this.size === 'md',
            'fix-title3-medium': this.size === 'lg',
        };
        const textWrapperClasses = {
            'gap-4': this.size === 'sm' || this.size === 'md',
            'gap-8': this.size === 'lg',
        };

        return html`
            <a
                href="${!this.disabled && this.link?.url ? this.link?.url : null}"
                element="choice chip"
                class="chip flex h-full w-full items-center rounded gap-fix-s ${classMap(chipClasses)}"
                ${optionalAttribute(this.disabled, 'aria-disabled', 'true')}
                ${optionalAttribute(this.checked, 'aria-current', 'true')}
                ${optionalAttribute(this.link?.trackingLabel, 'tracking-label', this.link?.trackingLabel)}
            >
                <slot name="leading">${this.iconTemplate(this.leadingIcon, classMap(leadingIconClasses))}</slot>
                <div class="flex min-w-0 flex-1 flex-col justify-center ${classMap(textWrapperClasses)}">
                    ${when(this.headline, html`<p class="m-0 ${classMap(headlineClasses)}">${this.headline}</p>`)}
                    ${when(this.text, html`<p class="m-0 fix-body3">${unsafeHTML(this.text)}</p>`)}
                </div>
                <slot name="trailing">${this.iconTemplate(this.trailingIcon, classMap(trailingIconClasses))}</slot>
            </a>
        `;
    }
}

defineElement('mb-choice-chip', ChoiceChip);
